import { format, parseISO } from 'date-fns';
import * as Calendar from 'expo-calendar';
import { Linking, Platform } from 'react-native';

import { Logger } from './logging';

export interface CalendarEventData {
  title: string;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
  location?: string;
  description?: string;
  confirmationCode?: string;
  /**
   * Translated confirmation code text (e.g., "Confirmation Code: ABC123")
   * If provided, this will be used instead of generating it from confirmationCode
   */
  confirmationCodeText?: string;
}

/**
 * Error keys for calendar operations
 */
export type CalendarErrorKey =
  | 'cannotOpenAppleCalendar'
  | 'cannotOpenGoogleCalendar'
  | 'cannotOpenYahooCalendar'
  | 'appleCalendarNotAvailable'
  | 'nativeCalendarNotAvailable'
  | 'unknownError';

/**
 * Result type for calendar operations
 */
export type CalendarResult =
  | { success: true }
  | { success: false; errorKey: CalendarErrorKey };

/**
 * Helper function to create a success result
 */
const successResult = (): CalendarResult => ({ success: true });

/**
 * Helper function to create an error result
 */
const errorResult = (errorKey: CalendarErrorKey): CalendarResult => ({
  success: false,
  errorKey,
});

/**
 * Builds the full description text including confirmation code if available
 * @param description - The main description text (may already include confirmation code)
 * @param confirmationCode - Raw confirmation code (used to check if already in description)
 * @param confirmationCodeText - Translated confirmation code text (e.g., "Confirmation Code: ABC123")
 *   Must be provided if confirmation code needs to be added and is not already in description
 */
const buildDescription = (
  description?: string,
  confirmationCode?: string,
  confirmationCodeText?: string,
): string => {
  // If description already includes confirmation code, return as-is
  if (
    description &&
    confirmationCode &&
    description.includes(confirmationCode)
  ) {
    return description;
  }

  // If no confirmation code needed, return description
  if (!confirmationCode && !confirmationCodeText) {
    return description || '';
  }

  // Only add confirmation code if we have translated text
  // If confirmationCode is provided but confirmationCodeText is not,
  // assume it's already in the description (from AppointmentDetailInfo)
  if (!confirmationCodeText) {
    return description || '';
  }

  // Use provided translated text
  return description
    ? `${description}\n\n${confirmationCodeText}`
    : confirmationCodeText;
};

/**
 * Creates the .ics file content for calendar events
 */
const createIcsContent = (event: CalendarEventData): string => {
  const startDate = format(parseISO(event.startDate), "yyyyMMdd'T'HHmmss");
  const endDate = format(parseISO(event.endDate), "yyyyMMdd'T'HHmmss");
  const description = buildDescription(
    event.description,
    event.confirmationCode,
    event.confirmationCodeText,
  );

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Squire//Appointment//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `SUMMARY:${event.title.replace(/[,;\\]/g, '\\$&')}`,
    ...(event.location
      ? [`LOCATION:${event.location.replace(/[,;\\]/g, '\\$&')}`]
      : []),
    ...(description
      ? [
          `DESCRIPTION:${description.replace(/\n/g, '\\n').replace(/[,;\\]/g, '\\$&')}`,
        ]
      : []),
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

/**
 * Downloads an .ics file on web via a Blob-backed anchor click.
 *
 * Safari (iOS and macOS) blocks `window.open()`/`location.href` navigation to
 * `data:` URIs as an anti-phishing measure, which is what `Linking.openURL`
 * uses under the hood on web — so that path silently does nothing on Safari.
 * A directly-clicked `<a download>` anchor isn't subject to that block, and
 * is the standard cross-browser technique for triggering calendar downloads.
 */
const downloadIcsFile = (event: CalendarEventData): void => {
  const icsContent = createIcsContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = 'appointment.ics';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

/**
 * Determines if Apple Calendar option should be shown
 * Returns true for:
 * - Native iOS (always works)
 * - Web (downloads an .ics file via data URI on any browser)
 * Returns false for:
 * - Native Android (no Apple Calendar integration)
 */
export const showAppleCalendar = (): boolean => {
  return Platform.OS === 'ios' || Platform.OS === 'web';
};

/**
 * Determines if Google Calendar option should be shown
 * Returns true for all platforms (Google Calendar works everywhere)
 */
export const showGoogleCalendar = (): boolean => {
  return true;
};

/**
 * Determines if Yahoo Calendar option should be shown
 * Returns true for all platforms (Yahoo Calendar works everywhere via web)
 */
export const showYahooCalendar = (): boolean => {
  return true;
};

/**
 * Opens the native calendar app with the event pre-filled
 * On iOS: Opens Apple Calendar with event edit view
 * On Android: Opens the system calendar app (typically Google Calendar) with event pre-filled
 */
const openNativeCalendar = async (
  event: CalendarEventData,
): Promise<CalendarResult> => {
  if (Platform.OS === 'web') {
    Logger.error('Native calendar is not available on web platform');
    return errorResult('nativeCalendarNotAvailable');
  }

  try {
    const startDate = parseISO(event.startDate);
    const endDate = parseISO(event.endDate);
    const description = buildDescription(
      event.description,
      event.confirmationCode,
    );

    await Calendar.createEventInCalendarAsync({
      title: event.title,
      startDate: startDate,
      endDate: endDate,
      location: event.location,
      notes: description,
    });

    return successResult();
  } catch (error) {
    Logger.error('Failed to open native calendar', { error, event });
    return errorResult('unknownError');
  }
};

/**
 * Creates a Google Calendar web URL for adding an event
 */
const createGoogleCalendarUrl = (event: CalendarEventData): string => {
  const startDate = format(parseISO(event.startDate), "yyyyMMdd'T'HHmmss");
  const endDate = format(parseISO(event.endDate), "yyyyMMdd'T'HHmmss");
  const description = buildDescription(
    event.description,
    event.confirmationCode,
    event.confirmationCodeText,
  );

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${startDate}/${endDate}`,
    details: description,
    location: event.location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Opens Apple Calendar with the event pre-filled
 * Only called when Apple Calendar is available (native iOS or web)
 * On iOS (native): Opens native Apple Calendar app
 * On web: Downloads an .ics file (any browser, including Safari)
 */
export const openAppleCalendar = async (
  event: CalendarEventData,
): Promise<CalendarResult> => {
  try {
    if (Platform.OS === 'web') {
      downloadIcsFile(event);
      return successResult();
    }

    // Native iOS
    return await openNativeCalendar(event);
  } catch (error) {
    Logger.error('Failed to open Apple Calendar', { error, event });
    return errorResult('unknownError');
  }
};

/**
 * Opens Google Calendar with the event details
 * On iOS: Opens Google Calendar web/app via URL
 * On Android: Opens native calendar app (typically Google Calendar)
 */
export const openGoogleCalendar = async (
  event: CalendarEventData,
): Promise<CalendarResult> => {
  try {
    // On Android, use native calendar (typically Google Calendar)
    if (Platform.OS === 'android') {
      return await openNativeCalendar(event);
    }

    // On iOS and web, use Google Calendar URL
    const url = createGoogleCalendarUrl(event);
    const canOpen = await Linking.canOpenURL(url);

    if (!canOpen) {
      Logger.error('Cannot open Google Calendar URL', { url });
      return errorResult('cannotOpenGoogleCalendar');
    }

    await Linking.openURL(url);
    return successResult();
  } catch (error) {
    Logger.error('Failed to open Google Calendar', { error, event });
    return errorResult('unknownError');
  }
};

/**
 * Creates a Yahoo Calendar web URL for adding an event
 */
const createYahooCalendarUrl = (event: CalendarEventData): string => {
  const startDate = format(parseISO(event.startDate), "yyyyMMdd'T'HHmmss'Z'");
  const description = buildDescription(
    event.description,
    event.confirmationCode,
    event.confirmationCodeText,
  );

  // Calculate duration in hours and minutes
  const start = parseISO(event.startDate);
  const end = parseISO(event.endDate);
  const durationMs = end.getTime() - start.getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor(
    (durationMs % (1000 * 60 * 60)) / (1000 * 60),
  );

  const duration =
    durationHours > 0
      ? `${String(durationHours).padStart(2, '0')}${String(durationMinutes).padStart(2, '0')}`
      : String(durationMinutes).padStart(2, '0');

  const params = new URLSearchParams({
    v: '60',
    view: 'd',
    type: '20',
    title: event.title,
    st: startDate,
    dur: duration,
    desc: description,
    in_loc: event.location || '',
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
};

/**
 * Opens Yahoo Calendar with the event details
 * Works on all platforms via Yahoo Calendar web URL
 */
export const openYahooCalendar = async (
  event: CalendarEventData,
): Promise<CalendarResult> => {
  try {
    const url = createYahooCalendarUrl(event);
    const canOpen = await Linking.canOpenURL(url);

    if (!canOpen) {
      Logger.error('Cannot open Yahoo Calendar URL', { url });
      return errorResult('cannotOpenYahooCalendar');
    }

    await Linking.openURL(url);
    return successResult();
  } catch (error) {
    Logger.error('Failed to open Yahoo Calendar', { error, event });
    return errorResult('unknownError');
  }
};
